/**
 * Decide whether a server-stored dark-mode preference (Firestore
 * `users/{uid}.preferences.darkMode`) should override the current in-app theme
 * when a user signs in / the app reloads.
 *
 * Rule: the per-device `localStorage['theme']` choice is AUTHORITATIVE. It is
 * the user's most recent explicit action on this device and must never be
 * overridden on refresh — doing so was the "theme resets to dark on reload" bug
 * (the server value kept reverting the toggle). The server value is therefore
 * only used to SEED a device that has no local choice yet (first login on a
 * fresh browser), enabling cross-device continuity without fighting the user.
 *
 * @param localStorageTheme value of `localStorage.getItem('theme')` ('dark' |
 *   'light' | null)
 * @param serverDarkMode the persisted preference (expected boolean, but treated
 *   defensively since it comes from Firestore)
 * @returns the boolean theme to apply, or `null` to keep the current theme.
 */
export function resolvePersistedTheme(
  localStorageTheme: string | null,
  serverDarkMode: unknown,
): boolean | null {
  // A local choice always wins — never override it on refresh.
  if (localStorageTheme !== null) return null;
  // No usable server preference → keep whatever theme is already applied.
  if (typeof serverDarkMode !== 'boolean') return null;
  // Fresh device, valid server preference → seed from it.
  return serverDarkMode;
}
